// Package Imports
import { message } from "antd";
import { useMutation } from "@apollo/client";
import React, { useLayoutEffect, useEffect, useState } from "react";

// Internal Imports
import { IResendOTP } from "../../../../types";
import { useResendOTP } from "../../hooks/useResendOTP";
import { FORGOT_PASSWORD } from "../../graphql/Mutations";
import { parseError } from "../../utils/parseError";

interface IResendOTPBlock extends IResendOTP {
  tailwindProps?: string;
  isOTP?: boolean;
}

const ResendOTPBlock = ({ username, clientID, tailwindProps, isOTP=true }: IResendOTPBlock) => {
  const [isVisible, setIsVisible] = useState(false);
  const { invokeResendOTP } = useResendOTP({ username, clientID });

  // MUTATION - FORGOT_PASSWORD
  const [forgotPasswordMutation, {
    loading: forgotPasswordLoading,
    data: forgotPasswordData,
    error: forgotPasswordError
  }] = useMutation(FORGOT_PASSWORD);

  // HANDLER - Forgot Password
  const invokeForgotPasswordResend = async () => {
    try {
      await forgotPasswordMutation({
        variables: {
          username: username
        }
      });
    } catch (err) {
      console.error("invokeForgotPasswordResend [ERROR]\n", err);
      message.error("We've encountered an error sending the reset code. Please try again later.");
    }
  }

  // SIDE-EFFECT - Evaluate Username before DOM
  useLayoutEffect(() => {
    if (username !== "") {
      setIsVisible(true);
    }
  }, [username]);

  // SIDE-EFFECT - Forgot Password [Success]
  useEffect(() => {
    // CHECK - Valid Forgot Password Data
    if (!forgotPasswordData) return;

    // VERIFY - Forgot Password Data
    if (forgotPasswordData?.forgotPassword?.Error) {
      message.error(
        typeof forgotPasswordData.forgotPassword.Error.message === "string"
          ? forgotPasswordData.forgotPassword.Error.message
          : "An error occurred sending the reset code."
      );
      return;
    }

    const processForgotPassword = async () => {
      try {
        if (forgotPasswordData?.forgotPassword) {
          message.success(`Reset code sent to ${username}`);
          return;
        }

        throw null;
      } catch (err) {
        console.error("processForgotPassword [ERROR]\n", err);
        message.error("We've encountered an error sending the reset code. Please try again later.");
      }
    }
    
    processForgotPassword();
  }, [forgotPasswordData]);

  // SIDE-EFFECT - Forgot Password [Failure]
  useEffect(() => {
    // CHECK - Valid Forgot Password Error
    if (!forgotPasswordError) return;

    const processForgotPasswordError = async () => {
      try {
        const errorMessage = parseError(forgotPasswordError);
        message.error(errorMessage);
      } catch (err) {
        console.error("processForgotPasswordError [ERROR]\n", err);
        message.error("Something went wrong... Please contact our support services");
      }
    }

    processForgotPasswordError();
  }, [forgotPasswordError]);

  return (
    <>
      {
        isVisible && (
          <div id="resend-otp-block" className={`flex justify-center ${ tailwindProps ? tailwindProps : "" }`}>
            <span>
              Didn't receive the code? <a className="text-tripplo-200 font-bold" href="#" onClick={() => isOTP ? invokeResendOTP() : invokeForgotPasswordResend()}>Resend Here</a>
            </span>
          </div>
        )
      }
    </>
  )
}

export default ResendOTPBlock;
